/**
 * React Hook for Firebase Cloud Messaging (FCM)
 * 
 * This hook provides an easy way to integrate FCM into React components
 * and manage push notification functionality.
 */

import { useState, useEffect, useCallback } from 'react';
import fcmService from '../Services/fcm';

export const useFCM = () => {
  // Capability checks to safely reference browser APIs
  const hasWindow = typeof window !== 'undefined';
  const hasNavigator = typeof navigator !== 'undefined';
  const hasNotification = hasWindow && 'Notification' in window;
  const hasServiceWorker = hasNavigator && 'serviceWorker' in navigator;
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  const [permission, setPermission] = useState(
    hasNotification ? Notification.permission : 'default'
  );

  /**
   * Initialize FCM service
   */
  const initializeFCM = useCallback(async () => {
    if (isInitialized || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      // Guard: FCM web push requires Notifications and Service Workers
      if (!hasNotification || !hasServiceWorker) {
        setError('Push notifications are not supported in this environment');
        setIsInitialized(false);
        return;
      }
      const success = await fcmService.initialize();
      if (success) {
        setIsInitialized(true);
        setToken(fcmService.getCurrentToken());
        if (hasNotification) setPermission(Notification.permission);
      } else {
        setError('Failed to initialize FCM service');
      }
    } catch (err) {
      setError(err.message || 'FCM initialization failed');
      console.error('FCM initialization error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, isLoading, hasNotification, hasServiceWorker]);

  /**
   * Request notification permission
   */
  const requestPermission = useCallback(async () => {
    try {
      if (!hasNotification) {
        // Not supported in this context (e.g., WebView)
        setError('Notifications API is not available');
        setPermission('denied');
        return 'denied';
      }
      const permission = await Notification.requestPermission();
      setPermission(permission);
      
      if (permission === 'granted' && isInitialized) {
        const newToken = await fcmService.requestPermissionAndGetToken();
        setToken(newToken);
      }
      
      return permission;
    } catch (err) {
      setError(err.message || 'Failed to request permission');
      throw err;
    }
  }, [isInitialized, hasNotification]);

  /**
   * Update push notification settings
   */
  const updateNotificationSettings = useCallback(async (enabled) => {
    try {
      const result = await fcmService.updatePushNotificationSettings(enabled);
      return result;
    } catch (err) {
      setError(err.message || 'Failed to update notification settings');
      throw err;
    }
  }, []);

  /**
   * Unregister FCM token
   */
  const unregisterToken = useCallback(async () => {
    try {
      await fcmService.unregisterToken();
      setToken(null);
      setIsInitialized(false);
    } catch (err) {
      setError(err.message || 'Failed to unregister token');
      throw err;
    }
  }, []);

  /**
   * Add event listener for FCM events
   */
  const addEventListener = useCallback((eventType, handler) => {
    const eventName = `aris-fcm-${eventType}`;
    window.addEventListener(eventName, handler);
    
    // Return cleanup function
    return () => {
      window.removeEventListener(eventName, handler);
    };
  }, []);

  /**
   * Check if notifications are supported
   */
  const isSupported = useCallback(() => {
    return hasNotification && hasServiceWorker;
  }, [hasNotification, hasServiceWorker]);

  /**
   * Get current notification permission status
   */
  const getPermissionStatus = useCallback(() => {
    return hasNotification ? Notification.permission : 'default';
  }, [hasNotification]);

  // Auto-initialization disabled - FCM is now initialized in ProtectedRoute
  // This prevents multiple components from triggering duplicate initializations
  useEffect(() => {
    // Poll to sync hook state with FCM service state
    const syncInterval = setInterval(() => {
      if (fcmService.isReady() && !isInitialized) {
        setIsInitialized(true);
        setToken(fcmService.getCurrentToken());
        if (hasNotification) setPermission(Notification.permission);
      }
    }, 100); // Check every 100ms

    // Also check immediately
    if (fcmService.isReady() && !isInitialized) {
      setIsInitialized(true);
      setToken(fcmService.getCurrentToken());
      if (hasNotification) setPermission(Notification.permission);
    }

    return () => clearInterval(syncInterval);
  }, [isInitialized, hasNotification]);

  // Listen for authentication changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'profile' || e.storageArea === sessionStorage) {
        // User logged out, cleanup FCM
        const authToken = document.cookie.includes('idtoken=');
        if (!authToken && isInitialized) {
          // User logged out, cleanup
          fcmService.cleanup();
          setIsInitialized(false);
          setToken(null);
          setError(null);
        }
        // Note: Login initialization is handled by ProtectedRoute
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isInitialized]);

  return {
    // State
    isInitialized,
    isLoading,
    error,
    token,
    permission,
    
    // Actions
    initializeFCM,
    requestPermission,
    updateNotificationSettings,
    unregisterToken,
    addEventListener,
    
    // Utilities
    isSupported,
    getPermissionStatus,
    
    // Service instance (for advanced usage)
    fcmService
  };
};

export default useFCM;
